import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import update from 'immutability-helper'

import { run, RuleRunner } from '../../../Shared/RuleRunner'
import {
  required, isEmail, isLimitedLength, isAlphanumeric,
} from '../../../Shared/Rules/Rules'
import Icon from '../../../Shared/Icon/Icon'
import InputField from '../../../Shared/InputField'
import TextareaField from '../../../Shared/TextareaField'

import CompetitionDropzone from '../CompetitionDropzone'
import SubmissionCategoriesContainer from './SubmissionCategoriesContainer'
import ThumbnailsContainer from './ThumbnailsContainer'

const fieldValidations = [
  RuleRunner('artworkName', 'Artwork name', required, isAlphanumeric, isLimitedLength),
  RuleRunner('userInfoFirstName', 'First name', required, isLimitedLength),
  RuleRunner('userInfoLastName', 'Last name', required, isLimitedLength),
  RuleRunner('userInfoEmail', 'Email', required, isEmail, isLimitedLength),
  RuleRunner('shortDescription', 'Short description', required, isLimitedLength),
  RuleRunner('portfolioUrl', 'URL to your portfolio website', required, isLimitedLength),
]

class SubmissionForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = this.getInitialState()

    this.onDrop = this.onDrop.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.onCategoryChange = this.onCategoryChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this)
    this.onSubmitFailure = this.onSubmitFailure.bind(this)
    this.errorFor = this.errorFor.bind(this)
  }

  getInitialState() {
    return {
      dropzoneActive: false,
      activeCategory: '',
      artworkName: '',
      shortDescription: '',
      portfolioUrl: '',
      userInfoFirstName: '',
      userInfoLastName: '',
      userInfoEmail: '',
      images: [],
      showErrors: false,
      validationErrors: {},
    }
  }

  componentWillMount() {
    this.setState({ validationErrors: run(this.state, fieldValidations) })
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ images: nextProps.images })
  }

  errorFor(field) {
    return this.state.validationErrors[field] || ''
  }

  onDrop(files) {
    this.setState({
      dropzoneActive: false,
    })

    this.props.openModal(files, true)
  }

  handleInputChange(e) {
    const newState = update(this.state, { [e.target.id]: { $set: e.target.value } })
    newState.validationErrors = run(newState, fieldValidations)

    this.setState(newState)
  }

  onCategoryChange(e, slug) {
    e.preventDefault()

    const newState = update(this.state, { activeCategory: { $set: slug } })
    newState.validationErrors = run(newState, fieldValidations)

    this.setState(newState)
  }

  handleSubmit(e) {
    e.preventDefault()

    this.setState({ showErrors: true })

    if (Object.getOwnPropertyNames(this.state.validationErrors).length !== 0 || this.state.images.length == 0) {
      return null
    }
    axios.post(`/api/internal/${this.props.competitionSlug}/competition-submissions`, {
      data: {
        type: 'competitionSubmission',
        attributes: {
          name: this.state.artworkName,
          email: this.state.userInfoEmail,
          firstName: this.state.userInfoFirstName,
          lastName: this.state.userInfoLastName,
          portfolioUrl: this.state.portfolioUrl,
          category: this.state.activeCategory,
          description: this.state.shortDescription,
        },
        relationships: {
          competitionImages: {
            data: Object.values(this.state.images).map((image) => {
              return { id: image.id, type: 'competitionImage' }
            }),
          },
        },
      },
    })
      .then(this.onSubmitSuccess)
      .catch(this.onSubmitFailure)
  }

  onSubmitSuccess(response) {
    App.events.publish('app:notification:flash:success', response.data.message)

    this.setState(this.getInitialState())

    this.props.clearImages()
    this.props.closeModal()
  }

  onSubmitFailure(response) {
    if (response.data) {
      App.events.publish('app:notification:flash:error', response.data.message)
    }
  }

  render() {
    const submissionCategories = I18n.t(`competition.${this.props.competitionSlug}.submission_form.categories`)
    const competition = I18n.t(`competition.${this.props.competitionSlug}.title`)

    return (
      <Modal
        isOpen={this.props.modalIsOpen}
        shouldCloseOnOverlayClick
        onRequestClose={this.props.closeModal}
        contentLabel="Modal"
        ariaHideApp={false}
        className={{
          base: 'overlay-modal--container',
          afterOpen: 'overlay-modal--container_after-open',
          beforeClose: 'overlay-modal--container_before-close',
        }}
        overlayClassName={{
          base: 'overlay-modal--background',
          afterOpen: 'overlay-modal--background_after-open',
          beforeClose: 'overlay-modal--background_before-close',
        }}
      >
        <div className="overlay-modal">
          <button className="btn btn-transparent overlay-modal--close-inner" onClick={() => this.props.closeModal()}>
            <Icon type={['far', 'times']} color="grey-dark" />
          </button>
          <div className="overlay-modal--content overlay-modal--competition">
            <div className="row">
              <div className="col-10 offset-1">
                <h2 className="content-heading__h2">
                  <div className="keyword-search-title">
                    Submission Form
                  </div>
                </h2>
                <p className="submission-form__description">
                  Fill in the submission form below and enter the competition! We kindly ask you to read the rules of the
                  CGTrader {competition} and properly tag your website/artwork before submitting your work.
                </p>
              </div>
            </div>
            <form>
              <div className="row">
                <div className="col-5 col-lg-4 offset-1 offset-lg-2">
                  <h3>Images</h3>
                  <CompetitionDropzone
                    dropzoneActive={this.state.dropzoneActive}
                    onDrop={this.onDrop}
                    openModal={this.props.openModal}
                    buttonClass="btn-outline-primary"
                  />

                  <hr />

                  <ThumbnailsContainer
                    images={this.state.images}
                    selectedImages={this.props.selectedImages}
                    mainImageId={this.props.mainImageId}
                    preloaders={this.props.preloaders}
                    onChangeMainImage={this.props.onChangeMainImage}
                    removeSelectedImages={this.props.onRemoveImages}
                    onCheckboxChange={this.props.onCheckboxChange}
                    loaderIcon={this.props.loaderIcon}
                    showError={this.state.showErrors}
                  />
                </div>
                <div className="col-5 col-lg-4">
                  <h3>Image info</h3>
                  <InputField
                    label="Name of the artwork"
                    required
                    id="artworkName"
                    name="artworkName"
                    errorText={this.errorFor('artworkName')}
                    showError={this.state.showErrors}
                    value={this.state.artworkName}
                    inputClassName="form-control-lg field--important"
                    onChange={this.handleInputChange}
                    autofocus
                  />
                  <TextareaField
                    label="Short description"
                    required
                    id="shortDescription"
                    name="shortDescription"
                    errorText={this.errorFor('shortDescription')}
                    showError={this.state.showErrors}
                    value={this.state.shortDescription}
                    inputClassName="form-control-lg field--important"
                    onChange={this.handleInputChange}
                  />
                  <InputField
                    label="URL to competition tag on your portfolio website "
                    required
                    id="portfolioUrl"
                    name="portfolioUrl"
                    errorText={this.errorFor('portfolioUrl')}
                    showError={this.state.showErrors}
                    value={this.state.portfolioUrl}
                    inputClassName="form-control-lg"
                    onChange={this.handleInputChange}
                    moreInfo="You are required to place a competition tag (text note or badge image or embed code) on
                    your portfolio website. Put an URL, where you have placed a competition tag."
                  />

                  <SubmissionCategoriesContainer
                    categories={submissionCategories}
                    activeCategory={this.state.activeCategory}
                    onCategoryChange={this.onCategoryChange}
                    showError={this.state.showErrors}
                  />

                  <hr />

                  <h3>Personal Info</h3>
                  <InputField
                    label="First Name"
                    required
                    id="userInfoFirstName"
                    name="userInfoFirstName"
                    errorText={this.errorFor('userInfoFirstName')}
                    showError={this.state.showErrors}
                    value={this.state.userInfoFirstName}
                    inputClassName="form-control-lg"
                    onChange={this.handleInputChange}
                  />
                  <InputField
                    label="Last Name"
                    required
                    id="userInfoLastName"
                    name="userInfoLastName"
                    errorText={this.errorFor('userInfoLastName')}
                    showError={this.state.showErrors}
                    value={this.state.userInfoLastName}
                    inputClassName="form-control-lg"
                    onChange={this.handleInputChange}
                  />
                  <InputField
                    label="Email"
                    required
                    id="userInfoEmail"
                    name="userInfoEmail"
                    errorText={this.errorFor('userInfoEmail')}
                    showError={this.state.showErrors}
                    value={this.state.userInfoEmail}
                    type="email"
                    inputClassName="form-control-lg"
                    onChange={this.handleInputChange}
                  />

                  <hr />

                  <button className="btn btn-primary btn-lg u-float-right" onClick={(e) => this.handleSubmit(e)}>Continue</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    )
  }
}

SubmissionForm.propTypes = {
  competitionSlug: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  onRemoveImages: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool,
  onChangeMainImage: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
  selectedImages: PropTypes.array.isRequired,
  mainImageId: PropTypes.number,
  preloaders: PropTypes.array.isRequired,
  clearImages: PropTypes.func.isRequired,
}

SubmissionForm.defaultPropTypes = {
  modalIsOpen: false,
}

export default SubmissionForm
